/**
 * Format a timestamp as a short human-readable relative string.
 *
 * Examples:
 *   "just now", "3m ago", "2h ago", "3d ago", "5w ago", "Mar 2026"
 */
const MINUTE = 60;
const HOUR = 60 * MINUTE;
const DAY = 24 * HOUR;
const WEEK = 7 * DAY;
const MONTH = 30 * DAY;

// Backend serializes some naive UTC datetimes without a tz designator (e.g.
// contacts.updated_at). JS parses such strings as local time, which would show
// "2h ago" for a just-saved row in CEST. Normalize to UTC when absent.
function parseIsoAsUtc(iso: string): Date {
  const hasTz = /Z$|[+-]\d{2}:?\d{2}$/.test(iso);
  return new Date(hasTz ? iso : iso + "Z");
}

export function formatRelative(iso: string | null | undefined, now: Date = new Date()): string {
  if (!iso) return "—";
  const t = parseIsoAsUtc(iso);
  if (isNaN(t.getTime())) return iso.slice(0, 10);
  const seconds = Math.max(0, Math.floor((now.getTime() - t.getTime()) / 1000));
  if (seconds < 45) return "just now";
  if (seconds < HOUR) return `${Math.round(seconds / MINUTE)}m ago`;
  if (seconds < DAY) return `${Math.round(seconds / HOUR)}h ago`;
  if (seconds < WEEK) return `${Math.round(seconds / DAY)}d ago`;
  if (seconds < MONTH) return `${Math.round(seconds / WEEK)}w ago`;
  // Older than a month: show abbreviated month + year.
  return t.toLocaleDateString(undefined, { month: "short", year: "numeric" });
}

export function daysSince(iso: string | null | undefined, now: Date = new Date()): number | null {
  if (!iso) return null;
  const t = parseIsoAsUtc(iso);
  if (isNaN(t.getTime())) return null;
  return Math.max(0, Math.floor((now.getTime() - t.getTime()) / (1000 * DAY)));
}
